<template>
    <section class="report-activity">
        <DiscussionTarget />
        <div class="report-section-title">
            <h6>Комментарии и история</h6>
            <b-button size="sm" variant="outline-primary" @click="showForm = !showForm">
                <i class="fas fa-plus"></i>
                Комментарий
            </b-button>
        </div>

        <div v-if="showForm" class="report-comment-form">
            <DiscussionEditor primary defer-clear ref="composer" :disabled="commentBusy"
                v-model="newComment"
                placeholder="Введите комментарий..."
            />
            <div class="mt-2">
                <MechanicAssetFileUploadPanel
                    v-model="newCommentFiles"
                    :disabled="commentBusy"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.txt"
                />
            </div>
            <div class="d-flex justify-content-end mt-2 report-comment-form__actions">
                <b-button size="sm" variant="outline-secondary" class="mr-2" @click="cancelAdd">
                    Отмена
                </b-button>
                <b-button size="sm" variant="primary" :disabled="commentBusy" @click="submitComment">
                    <i class="fas fa-paper-plane"></i>
                    Отправить
                </b-button>
            </div>
        </div>

        <div v-if="items.length" class="report-activity-list">
            <div v-for="item in items" :key="item._key" class="report-activity-item">
                <div v-if="item._type === 'history'" class="report-history-item">
                    <div class="report-activity-head">
                        <span>
                            <i class="fas fa-info-circle text-info mr-1"></i>
                            <strong><TelegramUserLink :user="item.User" fallback="Система" /></strong>
                            {{ getActionText(item.action) }}
                        </span>
                        <small class="text-muted">{{ formatDate(item.created_at) }}</small>
                    </div>
                    <DiscussionHistory v-if="item.description" :value="item.description" source-type="report_history" :source-id="item.id" />
                    <div v-if="renameReports(item).length" class="small mb-2">
                        <router-link v-for="id in renameReports(item)" :key="id" :to="'/reports/' + id" class="mr-3">Отчёт №{{ id }}</router-link>
                    </div>
                    <details v-if="renameChanges(item).length" class="report-rename-details">
                        <summary>Изменения имён ({{ renameChanges(item).length }})</summary>
                        <div v-for="change in renameChanges(item)" :key="change.media_id" class="report-rename-file">
                            <div><span class="text-muted">Было: </span>{{ change.old_name }}</div>
                            <div><span class="text-muted">Стало: </span>{{ change.new_name }}</div>
                            <div v-if="change.error" class="text-danger">{{ change.error }}</div>
                        </div>
                    </details>
                    <div v-for="(change, index) in mediaChanges(item)" :key="index" class="report-activity-media">
                        <div v-if="change.source_report_id && change.target_report_id" class="small mb-2">
                            <router-link :to="'/reports/' + change.source_report_id">Отчёт №{{ change.source_report_id }}</router-link>
                            <span class="mx-2">→</span>
                            <router-link :to="'/reports/' + change.target_report_id">Отчёт №{{ change.target_report_id }}</router-link>
                        </div>
                        <div v-if="change.source_reports && change.source_reports.length" class="small text-muted mb-2">
                            На момент повторного использования:
                            <span v-for="(source, sourceIndex) in change.source_reports" :key="source.id">
                                <span v-if="sourceIndex"> · </span>
                                <router-link :to="'/reports/' + source.id">№{{ source.id }}</router-link>
                                — {{ source.annulled ? 'аннулирован' : 'действующий' }}
                            </span>
                        </div>
                        <ReportMediaGrid v-if="change.files && change.files.length" :files="historyMediaFiles(change.files)" :report-id="reportId" :show-status="false" />
                    </div>
                </div>

                <div v-else>
                    <div class="report-activity-head">
                        <span>
                            <strong><TelegramUserLink :user="item.Author" fallback="Неизвестный" /></strong>
                            <b-badge v-if="item.is_system_comment" variant="info" class="ml-2">Системный</b-badge>
                        </span>
                        <span class="report-comment-actions">
                            <small class="text-muted">{{ formatDate(item.created_at) }}</small>
                            <button v-if="canEditComment(item) && !isEditing(item)" type="button" @click="startEdit(item)">
                                <i class="fas fa-pen"></i>
                            </button>
                            <button v-if="canEditComment(item) && !isEditing(item)" type="button" @click="deleteComment(item)">
                                <i class="fas fa-trash-alt"></i>
                            </button>
                        </span>
                    </div>
                    <DiscussionText v-if="!isEditing(item) && item.comment_text" :value="item.comment_text" source-type="report_comment" :source-id="item.id" />
                    <div v-else-if="isEditing(item)" class="mt-2">
                        <DiscussionEditor source-type="report_comment" :source-id="item.id" v-model="editedText" />
                        <div class="d-flex justify-content-end mt-2 report-comment-edit__actions">
                            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="cancelEdit">
                                Отмена
                            </b-button>
                            <b-button size="sm" variant="success" :disabled="commentBusy" @click="saveEdit(item)">
                                Сохранить
                            </b-button>
                        </div>
                    </div>
                    <FileList
                        v-if="item.Files && item.Files.length"
                        :list="item.Files"
                        :can-edit="canEditComment(item)"
                        class="mt-2"
                        @remove="fileId => deleteFile(item, fileId)"
                    />
                </div>
            </div>
        </div>
        <div v-else class="text-muted text-center py-3">
            Комментариев и событий пока нет
        </div>
    </section>
</template>

<script>
import {submitCommentFiles} from '@/utils/commentSubmission';
import {discussionFeedMixin} from "@/utils/discussions";
import TelegramUserLink from '@/views/Requests/RequestCard/components/TelegramUserLink.vue';
import MechanicAssetFileUploadPanel from '@/views/MechanicAssets/MechanicAssetFileUploadPanel.vue';
import FileList from '@/views/Requests/RequestCard/FileList/FileList.vue';
import ReportMediaGrid from './ReportMediaGrid.vue';
import linkifyText from '@/utils/Urlify';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';
import photoHistory from '../../../../shared/reportPhotoHistory';

const ACTION_TEXT = {
    photos_reused: 'использовал ранее загруженные фото',
    photos_reused_in_report: 'использовал фото в другом отчёте',
    photos_uploaded: 'добавил новые фото',
    photo_reuse_renamed: 'переименовала файлы для нового отчёта',
    photo_reuse_delayed: 'отложила переименование файлов',
    photo_reuse_recovered: 'восстановила переименование файлов',
    report_annulled: 'аннулировал отчёт',
    comment_added: 'добавил комментарий',
    comment_edited: 'отредактировал комментарий',
    comment_deleted: 'удалил комментарий',
    file_deleted: 'удалил файл',
    report_delivery_failed: 'зафиксировала сбой доставки отчёта',
    report_text_delivery_failed: 'зафиксировала сбой отправки текста отчёта',
    photo_upload_instruction_failed: 'зафиксировала сбой инструкции по загрузке фото',
    report_empty_photo_notification_failed: 'зафиксировала сбой уведомления об отсутствии фото',
    photo_delivery_delayed: 'зафиксировала задержку отправки фотографий',
    photo_delivery_failed: 'зафиксировала сбой обработки фотографий',
    photo_delivery_recovered: 'восстановила отправку фотографий'
};
const HIDDEN_FILE_ADDED_HISTORY_ACTIONS = ['files_added', 'note_files_added'];

export default {
    mixins: [discussionFeedMixin],
    name: 'ReportActivity',
    components: {
        ReportMediaGrid,
        TelegramUserLink,
        MechanicAssetFileUploadPanel,
        FileList
    },
    props: {
        comments: {
            type: Array,
            default: () => []
        },
        history: {
            type: Array,
            default: () => []
        },
        reportId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            showForm: false,
            newComment: '',
            newCommentFiles: [],
            editId: null,
            editedText: '',
            commentBusy: false
            ,submission: {}
        };
    },
    computed: {
        currentUser() {
            return this.$store.getters.currentUser || {};
        },
        isAdmin() {
            return this.$store.getters.isAdmin;
        },
        items() {
            const comments = (this.comments || []).map(item => ({
                ...item,
                _type: 'comment',
                _key: `comment_${item.id}`,
                _time: new Date(item.created_at || 0).getTime()
            }));
            const history = photoHistory.group(this.history)
                .filter(item => !HIDDEN_FILE_ADDED_HISTORY_ACTIONS.includes(item.action))
                .map(item => ({
                    ...item,
                    _type: 'history',
                    _key: `history_${item.id}`,
                    _time: new Date(item.created_at || 0).getTime()
                }));
            return comments.concat(history).sort((a, b) => b._time - a._time);
        }
    },
    methods: {
        mediaChanges(item) {
            return /^(photos_reused|photos_uploaded|photo_reuse_)/.test(item.action || '') ?
                (item.changes || []).filter(change => !change.old_name || !change.new_name) : [];
        },
        renameChanges(item) {
            return /^photo_reuse_/.test(item.action || '') ?
                (item.changes || []).filter(change => change.old_name && change.new_name) : [];
        },
        renameReports(item) {
            return [...new Set(this.renameChanges(item).reduce((ids, change) =>
                ids.concat(change.source_report_id, change.target_report_id), []).filter(Boolean))];
        },
        historyMediaFiles(files) {
            return files.map(file => ({...file, status: '', url: `/api/reports/${this.reportId}/media/${file.id}`,
                thumbnailUrl: `/api/reports/${this.reportId}/media/${file.id}/thumbnail`}));
        },
        formatLinkedText(text) {
            return linkifyText(text || '');
        },
        formatDate(value) {
            return value ? this.$moment(value).format('DD.MM.YYYY dd HH:mm') : '';
        },
        getActionText(action) {
            return ACTION_TEXT[action] || action || 'событие';
        },
        canEditComment(item) {
            if (!item || item.is_system_comment) return false;
            return this.isAdmin || Number(item.user_id) === Number(this.currentUser.userId);
        },
        isEditing(item) {
            return this.editId === item.id;
        },
        cancelAdd() {
            this.submission = {};
            this.newComment = '';
            this.newCommentFiles = [];
            this.showForm = false;
        },
        async submitComment() {
            if (this.commentBusy) return;
            const text = (this.newComment || '').trim();
            const files = this.newCommentFiles || [];
            if (!text && !files.length) {
                this.$bvToast.toast('Добавьте текст или файл', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            this.commentBusy = true;
            try {
                const id = this.reportId;
                await submitCommentFiles(this.submission, text, files, {
                    create: value => this.$store.dispatch('reports/addComment', {id, text: value}),
                    update: (commentId, value) => this.$store.dispatch('reports/editComment', {id, commentId, text: value}),
                    upload: (commentId, file) => this.$store.dispatch('reports/uploadCommentFiles', {id, commentId, files: [file]})
                });
                notifyInstallPromotionSuccess({source: 'report-comment-added'});
                this.$refs.composer.clear();
                this.cancelAdd();
                this.$emit('changed');
            } catch (error) {
                this.$refs.composer.error = error.response && error.response.data && error.response.data.message || 'Не удалось сохранить комментарий и вложения. Повторите попытку.';
            } finally {
                this.commentBusy = false;
            }
        },
        startEdit(item) {
            this.editId = item.id;
            this.editedText = item.comment_text || '';
        },
        cancelEdit() {
            this.editId = null;
            this.editedText = '';
        },
        async saveEdit(item) {
            const text = (this.editedText || '').trim();
            if (!text) {
                this.$bvToast.toast('Комментарий не может быть пустым', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            this.commentBusy = true;
            try {
                await this.$store.dispatch('reports/editComment', {
                    id: this.reportId,
                    commentId: item.id,
                    text
                });
                notifyInstallPromotionSuccess({source: 'report-comment-edited'});
                this.cancelEdit();
                this.$emit('changed');
            } finally {
                this.commentBusy = false;
            }
        },
        deleteComment(item) {
            this.$bvModal.msgBoxConfirm('Удалить комментарий?', {
                okTitle: 'Удалить',
                cancelTitle: 'Отмена',
                okVariant: 'danger'
            }).then(async confirmed => {
                if (!confirmed) return;
                this.commentBusy = true;
                try {
                    await this.$store.dispatch('reports/deleteComment', {
                        id: this.reportId,
                        commentId: item.id
                    });
                    notifyInstallPromotionSuccess({source: 'report-comment-deleted'});
                    this.$emit('changed');
                } finally {
                    this.commentBusy = false;
                }
            });
        },
        async deleteFile(item, fileId) {
            this.commentBusy = true;
            try {
                await this.$store.dispatch('reports/deleteCommentFile', {
                    id: this.reportId,
                    commentId: item.id,
                    fileId
                });
                notifyInstallPromotionSuccess({source: 'report-comment-file-deleted'});
                this.$emit('changed');
            } finally {
                this.commentBusy = false;
            }
        }
    }
}
</script>

<style scoped>
.report-rename-details {
    margin-top: 8px;
    min-width: 0;
}
.report-rename-details summary {
    cursor: pointer;
    padding: 6px 0;
    color: #315b7c;
}
.report-rename-file {
    padding: 8px 0;
    border-top: 1px solid #e4eaf0;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: 12px;
}
.report-activity {
    border-top: 1px solid #dee2e6;
    padding-top: 14px;
}

.report-section-title {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}

.report-section-title h6 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
}

.report-comment-form {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    margin-bottom: 12px;
    padding: 10px;
}

.report-activity-list {
    display: grid;
    gap: 10px;
}

.report-activity-item {
    border-bottom: 1px solid #eef1f4;
    padding-bottom: 10px;
}

.report-activity-head {
    align-items: flex-start;
    display: flex;
    gap: 8px;
    justify-content: space-between;
}

.report-activity-text {
    margin-top: 6px;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.report-history-item {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 8px;
}

.report-comment-actions {
    align-items: center;
    display: inline-flex;
    gap: 8px;
}

.report-comment-actions button {
    background: transparent;
    border: 0;
    color: #6c757d;
    padding: 0;
}

.report-comment-actions button:hover {
    color: #0056b3;
}

@media (max-width: 767.98px) {
    .report-activity {
        padding-top: 10px;
    }

    .report-section-title {
        align-items: stretch;
        flex-direction: column;
        gap: 8px;
    }

    .report-section-title .btn {
        width: 100%;
    }

    .report-comment-form {
        padding: 8px;
    }

    .report-comment-form__actions,
    .report-comment-edit__actions {
        gap: 8px;
    }

    .report-comment-form__actions .btn,
    .report-comment-edit__actions .btn {
        flex: 1 1 0;
        margin-right: 0 !important;
    }

    .report-activity-head {
        flex-direction: column;
        gap: 4px;
    }

    .report-comment-actions {
        align-self: stretch;
        justify-content: space-between;
        width: 100%;
    }

    .report-comment-actions button {
        min-height: 32px;
        min-width: 32px;
    }
}
</style>
